import { useState, useEffect } from "react";

const HeadTile = ({ id, name, familyHeads, familyHeadsSetter }) => {
  const [checked, setChecked] = useState(false);

  // Sync local state with shared familyHeads (important if state is changed externally)
  useEffect(() => {
    setChecked(familyHeads.includes(id));
  }, [familyHeads, id]);

  const handleToggle = (newChecked) => {
    setChecked(newChecked);

    familyHeadsSetter((prev) => {
      if (newChecked) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        return prev.filter((item) => item !== id);
      }
    });
  };

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        handleToggle(!checked);
      }}
      style={{
        width: "90%",
        margin: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: checked ? "#ffd1d1ff" : "#f1f3f8",
        borderRadius: "8px",
        padding: "8px 12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: "20%",
          textAlign: "center",
          fontWeight: "600",
          color: "#333",
        }}
      >
        {id}
      </div>

      <div
        style={{
          width: "75%",
          paddingLeft: "10px",
          fontWeight: "500",
          color: "#444",
        }}
      >
        {name}
      </div>

      <input
        type="checkbox"
        checked={checked}
        onClick={(e) => e.stopPropagation()} // prevent parent from firing
        onChange={(e) => handleToggle(e.target.checked)}
        style={{
          transform: "scale(1.2)",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

const LocationTile = ({ familyHeads, familyHeadsSetter, barangayName, heads }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      onClick={() => setExpanded(!expanded)}
      style={{
        padding: "5px 15px 10px 15px",
        marginTop: "10px",
        width: "80%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#e3e8f0",
        borderRadius: "12px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        cursor: "pointer",
        transition: "background-color 0.3s ease, transform 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#d2d9e3";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#e3e8f0";
      }}
    >
      <p
        style={{
          fontWeight: "600",
          color: "#222",
          fontSize: "15px",
          marginBottom: expanded ? "8px" : "0",
        }}
      >
        {barangayName}
      </p>
      {expanded && (
        <div style={{ animation: "fadeIn 0.3s ease-in-out", display: "flex", flexDirection: "column", rowGap: "0.25rem"}}>
          {
            heads.length !== 0 &&
            heads.map((head) => {
              return <HeadTile key={head.headId} id={head.headId} name={head.name} familyHeads={familyHeads} familyHeadsSetter={familyHeadsSetter} />
            })
          }
        </div>
      )}
    </div>
  );
};

const DeleteRequest = () => {
  const [deleteRequests, setDeleteRequests] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);

  const [familyHeads, setFamilyHeads] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const [refresh, setRefresh] = useState(Date.now());

  const deleteHead = async (headId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/mass-upload/${headId}`,
        {
          method: "DELETE",
          headers: { "x-api-key": process.env.REACT_APP_API_KEY },
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Delete failed.");
      }
      return result; 
    } catch (error) {
      alert(`Delete failed: ${error.message}`);
      throw error; 
    }
  };

  const handleDeleteClick = async () => {
    if (familyHeads.length === 0) {
      alert("No family heads selected.");
      return;
    }
    setSelectedStatus("approved");
    setShowConfirm(true);
  };

  const handleRejectClick = () => {
    if (familyHeads.length === 0) {
      alert("No family heads selected for deletion.");
      return;
    }
    setSelectedStatus("rejected");
    setShowConfirm(true);
  };

  const updateStatus = async (status) => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/delete_requests`;
    const response = await fetch(baseUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": process.env.REACT_APP_API_KEY,
      },
      body: JSON.stringify({ status: status, heads: familyHeads }),
    });

    let result;
    try {
      result = await response.json();
    } catch {
      const text = await response.text();
      console.error("Non-JSON response:", text);
      throw new Error(text);
    }

    if (!response.ok) {
      console.error("Server error response:", result);
      throw new Error(result.error || `Failed to update data. Status: ${response.status}`);
    }

    return result.status;
  };

  const fetchDeleteRequests = async () => {
      let baseUrl = `${process.env.REACT_APP_API_URL}/api/locations/api/delete_requests`
      try {
          const apiUrl = `${baseUrl}`;
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'x-api-key': process.env.REACT_APP_API_KEY
            }
          });
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
              setDeleteRequests(data);
              return data;
          } else {
              console.warn(`API returned an empty array or non-array data. Data received:`, data);
              // Default empty fetchedData if no data
          }
    } catch (error) {
      console.error(`Error fetching delete requests:`, error);
      // Default empty fetchedData on error
    }
  }

  useEffect(() => {
    const loadDeleteRequests = async () => {
      const data = await fetchDeleteRequests();
      const grouped = (data) ? Object.values(
        data.reduce((acc, { barangay_address, headname, head_id }) => {
          if (!acc[barangay_address]) {
            acc[barangay_address] = { barangay: barangay_address, heads: [] };
          }
          acc[barangay_address].heads.push({ name: headname, headId: head_id });
          return acc;
        }, {})
      ).sort((a, b) => a.barangay - b.barangay) : [];
      setDeleteRequests(grouped);
    }

    loadDeleteRequests();
  }, [refresh])

  const confirmDelete = async () => {
    try {
      await Promise.all(familyHeads.map((headId) => deleteHead(headId)));
      setSelectedStatus("approved");
      setShowConfirm(true);
    } catch (error) {
      alert("Some deletions failed. Please check your network or try again.");
    }
    alert(`Deleted ${familyHeads.length} family head(s).`);
    await updateStatus('approved');
    setRefresh(Date.now()); // refetch 
    setFamilyHeads([]); // clear selections
    setShowConfirm(false);
  };

  const confirmReject = async () => {
    alert(`Rejected delete of ${familyHeads.length} family head(s).`);
    await updateStatus('rejected');
    setRefresh(Date.now()); // refetch 
    setFamilyHeads([]); // clear selections
    setShowConfirm(false);
  };

  return (
    (deleteRequests.length !== 0) ?
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        width: "50vw",
        alignItems: "center",
        margin: "50px auto",
      }}
    >
      <div style={{display: "flex", width: "85%", columnGap: "30px"}}>
        {/* Button for deleting heads */}
        <span
          style={{
            border: "2px solid #444",
            width: "100%",
            padding: "10px",
            borderRadius: "10px",
            backgroundColor: "#becdde",
            fontWeight: "700",
            textAlign: "center",
            fontSize: "18px",
            color: "#222",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e1a9a9ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#becdde";
          }}
          onClick={handleDeleteClick}
        >
          {`Delete Heads ${familyHeads.length !== 0 ? `(${familyHeads.length})` : ""}`}
        </span>
        {/* Button for rejecting delete request */}
        <span
          style={{
            border: "2px solid #444",
            width: "100%",
            padding: "10px",
            borderRadius: "10px",
            backgroundColor: "#becdde",
            fontWeight: "700",
            textAlign: "center",
            fontSize: "18px",
            color: "#222",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#e1a9a9ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#becdde";
          }}
          onClick={handleRejectClick}
        >
          {`Reject Request ${familyHeads.length !== 0 ? `(${familyHeads.length})` : ""}`}
        </span>
      </div>
      {Object.keys(deleteRequests).length !== 0 && deleteRequests.map((item) => {
        return <LocationTile key={item.barangay} familyHeads={familyHeads} familyHeadsSetter={setFamilyHeads} barangayName={item.barangay} heads={item.heads} />
      })}

      {showConfirm && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: "1000",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px 30px",
              borderRadius: "12px",
              textAlign: "center",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
              minWidth: "300px",
            }}
          >
            <h3 style={{ marginBottom: "15px", color: "#333" }}>
              {`${selectedStatus === "approved" ? "Confirm Delete" : "Confirm Reject"}`} 
            </h3>
            <p style={{ marginBottom: "20px", color: "#555" }}>
              {`Are you sure you want to ${selectedStatus === "approved" ? "delete" : "reject"} ${familyHeads.length} family head(s)?`}
            </p>
            <div style={{ display: "flex", justifyContent: "space-around" }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#ccc",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={selectedStatus === "approved" ? confirmDelete : confirmReject}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: "#e74c3c",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    :
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        width: "50vw",
        margin: "50px auto",
        backgroundColor: "#f9fafb",
        padding: "40px",
        borderRadius: "12px",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "48px", color: "#9ca3af", marginBottom: "12px" }}>
        🗂️
      </div>
      <h2 style={{ color: "#374151", marginBottom: "8px" }}>
        No Delete Requests
      </h2>
      <p style={{ color: "#6b7280", fontSize: "0.95rem" }}>
        There are currently no delete requests available.
      </p>
    </div>
  );
};

export default DeleteRequest;
